import {HomeOutlined, UserOutlined} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Form,
  Input,
  Spin,
} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RQGetProfileAPI,
  RQUpdateAdminProfileAPI,
  RQUpdateUserProfileAPI,
} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

const ProfileScreen = () => {
  const [isEditing, setIsEditing] = useState(false);

  const [form] = Form.useForm();

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const decodedToken = jwtDecode(token);

  const id = decodedToken?.userId;

  const {
    data: profileDetails,
    isLoading,
    refetch,
    error,
  } = RQGetProfileAPI(id, token);

  const updateAPI = RQUpdateUserProfileAPI;

  const {mutate: updateProfile} = updateAPI({
    onSuccess: response => {
      if (response) {
        refetch();
        setIsEditing(false);
        toast.success('Profile Updated Successfully!!');
      } else {
        toast.error(response?.message || 'Something went wrong!!');
      }
    },
    onError: error => {
      toast.error(error?.message);
    },
  });

  useEffect(() => {
    if (profileDetails) {
      form.setFieldsValue(profileDetails);
    }
  }, [profileDetails, form]);

  const onFinish = values => {
    updateProfile({
      token,
      formData: values,
      id: decodedToken?.userId,
    });
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-full'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (error) {
    return <Alert message={'Failed to load users'} type={'error'} />;
  }

  return (
    <div className={'md:p-6 p-3'}>
      <div>
        <Breadcrumb
          className={'md:flex hidden'}
          items={[
            {
              href: '/',
              title: <HomeOutlined />,
            },
            {
              href: '',
              title: (
                <>
                  <UserOutlined />
                  <span>Profile</span>
                </>
              ),
            },
          ]}
        />
      </div>

      <div
        className={
          'flex flex-col justify-self-center items-center md:w-1/2 h-full'
        }
      >
        <h1 className={'text-4xl mb-4'}>Profile</h1>
        <div className={'w-full flex justify-center items-center'}>
          <Card className={'w-full'}>
            <Form
              form={form}
              layout={'vertical'}
              onFinish={onFinish}
              initialValues={profileDetails}
            >
              <div className={'flex justify-between items-center md:mb-6'}>
                <div
                  className={'md:flex flex flex-row md:items-center md:gap-4'}
                >
                  <Avatar size={64}>
                    {profileDetails?.firstName?.[0]}
                    {profileDetails?.lastName?.[0]}
                  </Avatar>
                  <div>
                    <p className={'text-gray-600 text-xs'}>
                      {decodedToken?.role}
                    </p>
                    <p className={'font-semibold md:text-lg'}>
                      {profileDetails?.firstName} {profileDetails?.lastName}
                    </p>
                    <p className={'text-gray-600'}>{profileDetails?.email}</p>
                  </div>
                </div>
                <div
                  className={
                    'flex md:flex-row flex-col items-center gap-2 md:gap-4'
                  }
                >
                  <h1 className={'text-green-500'}>Active</h1>
                  {!isEditing && (
                    <Button type={'primary'} onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              <div className={'grid grid-cols-1 md:grid-cols-2 gap-2'}>
                <Form.Item
                  label={'First Name'}
                  name={'firstName'}
                  rules={[
                    {required: true, message: 'First name is required'},
                    {whitespace: true, message: ''},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                  ]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>

                <Form.Item
                  label={'Last Name'}
                  name={'lastName'}
                  rules={[
                    {required: true, message: 'Last name is required'},
                    {whitespace: true, message: ''},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                  ]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>

                <Form.Item
                  label={'Mobile'}
                  name={'mobile'}
                  rules={[
                    {required: true, message: 'Enter contact number'},
                    {pattern: /^[0-9]{10}$/, message: 'Must be 10 digits'},
                  ]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>

                <Form.Item
                  label={'Pin Code'}
                  name={'pinCode'}
                  rules={[
                    {required: true},
                    {pattern: /^[0-9]{6}$/, message: 'Must be 6 digits'},
                  ]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>

                <Form.Item
                  label={'Address'}
                  name={'address'}
                  rules={[{required: true}, {whitespace: true, message: 'Address is required'}]}
                  className={'col-span-1 md:col-span-2'}
                >
                  <Input.TextArea rows={3} disabled={!isEditing} />
                </Form.Item>

                <Form.Item
                  label={'City'}
                  name={'city'}
                  rules={[{required: true}, {whitespace: true, message: 'City is required'}]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>

                <Form.Item
                  label={'District'}
                  name={'district'}
                  rules={[{required: true}, {whitespace: true, message: 'District is required'}]}
                >
                  <Input disabled={!isEditing} />
                </Form.Item>
              </div>

              {isEditing && (
                <div className={'flex justify-end gap-4 mt-2'}>
                  <Button
                    onClick={() => {
                      form.resetFields();
                      form.setFieldsValue(profileDetails);
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    htmlType={'submit'}
                    type={'primary'}
                    className={'!bg-primary !text-white'}
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </Form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
